import { useDareContext } from 'root/src/client/web/list/listItemComponents/dare/contexts/dare'
import { COMING_SOON_MASK_TEXT } from 'root/src/shared/constants/dropEvent'
import {
	useMemo,
	useState,
} from 'react'
import { statusNamesEnum } from 'root/src/client/web/list/listItemComponents/project/ProjectCardLabel'
import {
	projectAcceptedKey,
	projectApprovedKey,
	projectDeliveredKey,
	projectDeliveryPendingKey,
	projectPendingKey,
	projectRejectedKey,
} from 'root/src/shared/constants/keys'
import moment from 'moment-mini'
import { notNil } from 'root/src/shared/util/ramdaPlus'
import { getConnectedPlatforms } from 'root/src/client/web/record/util/getConnectedPlatforms'
import {
	useBlackCardContext,
} from 'root/src/client/v2/common/components/BlackCard/hooks/useBlackCardContext'
import { useUser } from 'root/src/client/logic/auth/hooks/useUser'
import { useIsMaxDaresReachedByPlatforms } from 'root/src/client/logic/dropEvent/hooks/useIsMaxDaresReached'
import { useMaxDaresByPlatforms } from 'root/src/client/logic/dropEvent/hooks/useMaxDaresByPlatforms'
import { RewardType } from 'root/src/shared/rewards/constants'
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated'
import { ProjectStatus } from 'root/src/shared/@types/project'
import apiRequest from 'root/src/client/logic/api/thunks/apiRequest'
import { dropEndpointIds } from 'root/src/shared/drops/drops.constants'
import { useAppDispatch } from 'root/src/client/logic/helpers/useStore'
import { useRecordId } from 'root/src/client/v2/common/hooks/useRecordId'
import { getClassifiedDarePlatform } from 'root/src/shared/drops/helpers/dropDare.helpers'
import { isProjectSubmittable } from 'root/src/client/logic/project/helpers/project.helpers'
import { DarePlatform } from 'root/src/shared/@types/platforms'

/**
 * Returns dare from contexts
 * @return {import('root/src/shared/@types/dropEvent').DropEventDare}
 */
export const useDare = () => {
	const dareCardContext = useDareContext()

	const { dare } = dareCardContext

	if (dareCardContext.comingSoon) {
		return {
			...dare,
			product: {
				...dare.product,
				name: COMING_SOON_MASK_TEXT,
			},
			title: COMING_SOON_MASK_TEXT,
			description: COMING_SOON_MASK_TEXT,
		}
	}

	return dare
}

const getDareCardLabels = (dare, ctx) => {
	const { status } = dare

	const timeLeftMod = ctx.timeLeft
		|| ctx.expirationDate && moment(ctx.expirationDate).diff(moment().format(), 'seconds')

	const pendingStatus = status === projectPendingKey
	const acceptedStatus = status === projectAcceptedKey
	const deliveryPendingStatus = status === projectDeliveryPendingKey
	const completedStatus = status === projectDeliveredKey
	const expiredStatus = !completedStatus && timeLeftMod <= 0
	const rejectedStatus = status === projectRejectedKey

	return {
		[statusNamesEnum.pending]: pendingStatus,
		[statusNamesEnum.accepted]: acceptedStatus,
		[statusNamesEnum.inReview]: deliveryPendingStatus,
		[statusNamesEnum.delivered]: completedStatus,
		[statusNamesEnum.rejected]: rejectedStatus,
		[statusNamesEnum.expired]: expiredStatus,
	}
}

const isInteracted = (ctx) => {
	const acceptedStatus = (!ctx.projectDeliveries
		&& !ctx.projectCompleted
		&& !ctx.projectDeliveryPending
		&& ctx.projectAccepted) || ctx.projectStatus === projectApprovedKey
	const completedStatus = ctx.projectDeliveries || ctx.projectCompleted
	const expiredStatus = !completedStatus && ctx.timeLeft <= 0
	const deliveryPendingStatus = ctx.projectDeliveryPending
	const noPrizesStatus = notNil(ctx.prizeAvailable) ? !ctx.prizeAvailable : false
	const rejectedStatus = ctx.projectStatus === projectRejectedKey
	const pendingStatus = ctx.projectStatus === projectPendingKey
	const interactedStatus = acceptedStatus || completedStatus || pendingStatus
		|| expiredStatus || deliveryPendingStatus || noPrizesStatus || rejectedStatus

	return Boolean(interactedStatus)
}

export const useDareCard = () => {
	const [isLoading, setIsLoading] = useState(false)
	const user = useUser()
	const blackCardContext = useBlackCardContext()
	const dareCardContext = useDareContext()
	const recordId = useRecordId()
	const dispatch = useAppDispatch()
	const dare = useDare()
	const maxDaresByDarePlatform = useMaxDaresByPlatforms({ platforms: dare.platforms, fallbackDrop: dareCardContext.drop })
	const isMaxDaresReached = useIsMaxDaresReachedByPlatforms({ fallbackDrop: dareCardContext.drop })
	const isAuthenticated = useIsAuthenticated()
	const { isAbleToParticipate = true, canAccessCreateBrandProject } = dareCardContext

	const isDarePlatformAvailable = useMemo(() => {
		if (dare.platforms.includes(DarePlatform.X)) {
			return true
		}

		const userPlatforms = getConnectedPlatforms(user?.userData?.tokens ?? dareCardContext?.tokens)

		if (dareCardContext.canAccessCreateBrandProject) {
			return true
		}

		return dare.platforms.some(darePlatform => userPlatforms.includes(getClassifiedDarePlatform(darePlatform)))
	}, [dare, dareCardContext.platform, dareCardContext.tokens, dareCardContext.canAccessCreateBrandProject, user?.userData?.tokens])

	const labelsMap = getDareCardLabels(dare, dareCardContext)

	const onEmailSubscribe = async () => {
		setIsLoading(true)
		try {
			await dareCardContext.onEmailSubscribe()
		} catch (err) {
			console.error(err)
		}
		setIsLoading(false)
	}

	const onAccept = async () => {
		setIsLoading(true)
		try {
			await dareCardContext.onClick(blackCardContext.setIsHovered)
			await refetchDropEvent()
		} catch (err) {
			console.error(err)
		}
		setIsLoading(false)
	}

	const refetchDropEvent = async () => dispatch(
		apiRequest(dropEndpointIds.DROP_GET_EVENT, { recordId }),
	)

	const creatorDareTimeLeft = dare.creatorDareExpirationDate ? moment(dare.creatorDareExpirationDate).diff(moment().format(), 'seconds') : undefined
	const dareTimeLeft = dare.expirationDate ? moment(dare.expirationDate).diff(moment().format(), 'seconds') : undefined

	const canSubmitVideo = isProjectSubmittable(dare)
	const isAccepted = Boolean(dare.projectId) && (
		dare.status === ProjectStatus.ProjectAccepted
		|| dare.status === ProjectStatus.DeliveryPending
		|| dare.status === ProjectStatus.DeliveryRejected
	)

	const isDareButtonEnabled = getIsDareButtonEnabled({
		canAccessCreateBrandProject,
		isAuthenticated,
		maxDares: maxDaresByDarePlatform,
		isAbleToParticipate,
		isDarePlatformAvailable,
		isMaxDaresReached,
		isComingSoon: dareCardContext.comingSoon,
		dareTimeLeft,
		isLockedByTier: dareCardContext.isLockedByTier,
		isDeliverable: dare.status === ProjectStatus.ProjectAccepted
			|| dare.status === ProjectStatus.Rejected
			|| dare.status === ProjectStatus.DeliveryPending,
		isAccepted,
		canSubmitVideo,
	})

	return {
		dare,
		actions: {
			goToDareForm: dareCardContext.redirectAndPrePopulate,
			onAccept,
			onEmailSubscribe,
			refetchDropEvent,
		},
		extraData: {
			dareCardContext,
			brand: dareCardContext.brand,
			game: dare.games[0],
			user: {
				userId: user?.authentication?.userId,
				isDareButtonEnabled,
				isMaxDaresReached,
				isAbleToParticipate,
				isEmailSubscribing: dareCardContext.emailSubscription,
				isEligibleToEarnPoints:
					dareCardContext.isEligibleToEarnPoints && dareCardContext.pointsToEarn > 0,
				isEligibleToCreateDare: dareCardContext.canAccessCreateBrandProject,
				isAdmin: dareCardContext.isAdmin,
				pointsToEarn: dareCardContext.pointsToEarn,
				maxDares: maxDaresByDarePlatform,
				isDarePlatformAvailable,
				isAuthenticated,
				isLockedByTier: dareCardContext.isLockedByTier,
			},
			campaignId: dareCardContext.campaignId,
			labels: Object.entries(labelsMap).filter(([, value]) => value).map(([key]) => key),
			isOutOfStock: !dareCardContext?.dare?.prizeAvailable
				&& !dareCardContext.projectAccepted
				&& !dareCardContext.comingSoon,
			creatorDareTimeLeft,
			dareTimeLeft,
			isInteracted: isInteracted(dareCardContext),
			isInteractable: !dare.projectId || (dare.status !== ProjectStatus.ProjectDelivered),
			isBundle: dare.product.type === RewardType.BUNDLE,
			isAccepted,
			isExternal: dare.product.type === RewardType.EXTERNAL_API_FULFILLED,
			isDelivered: Boolean(dare.projectId && dare.status === ProjectStatus.ProjectDelivered),
			canSubmitVideo,
		},
		isComingSoon: dareCardContext.comingSoon,
		isLoading,
	}
}

/**
 * @typedef Dare
 * @type {Object}
 * @property {string} title
 * @property {string} description
 * @property {string} status
 * @property {string} type
 * @property {string} level
 * @property {string} rewardFor
 * @property {string} [platform]
 * @property {string} [hint]
 * @property {string} [estimatedTime]
 * @property {string} [dareTimeLeft]
 * @property {{ id: string, label: string, image: string }[]} games
 * @property {{ id: string, name: string, type: string  }} product
 * @property {{ available: number, used: number }} capacity
 */

/**
 * Returns true if dare button is enabled
 * @param {{ canAccessCreateBrandProject: boolean, isAuthenticated: boolean, maxDares: number, isAbleToParticipate: boolean, isDarePlatformAvailable: boolean, isMaxDaresReached: boolean, isComingSoon: boolean, isLockedByTier: boolean, isDeliverable: boolean, dareTimeLeft: number, isAccepted: boolean, canSubmitVideo: boolean }} params
 * @return {boolean}
 */
function getIsDareButtonEnabled(params) {
	if (typeof params.dareTimeLeft === 'number' && params.dareTimeLeft <= 0) {
		return false
	}
	if (params.isComingSoon) {
		return true
	}
	if (params.isAccepted && !params.canSubmitVideo) {
		return false
	}
	if (params.isDeliverable) {
		return true
	}
	if (params.canAccessCreateBrandProject || !params.isAuthenticated) {
		return true
	}
	if (!params.maxDares || !params.isAbleToParticipate || !params.isDarePlatformAvailable || params.isMaxDaresReached || params.isLockedByTier) {
		return false
	}
	return true
}
